(() => {
  if (window.__sbHeroVariantMediaLoaded) return;
  window.__sbHeroVariantMediaLoaded = true;

  const controllers = new Map();
  const selector = '[data-hero-variant-media]';
  const initializeHeroMedia = (root) => {
    if (controllers.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const device = () => mobileQuery.matches ? 'mobile' : 'desktop';
    const frames = new Map();
    const presentedTimes = new WeakMap();
    let preparing = null;
    let trackingVideo = null;
    let trackingCallback = null;
    let variantId = root.dataset.currentVariantId;
    let active = root.querySelector('[data-hero-video-active]');
    if (!active) return;
    active.dataset.heroVideoKey = mobileQuery.matches ? active.dataset.mobileKey : active.dataset.desktopKey;
    frames.set(active.dataset.heroVideoKey, active);

    // Decoder recovery can replace a registered video, so always query the live element.
    const videoFor = (frame) => frame?.querySelector('video');
    const shouldPlay = () => window.SBMediaLoader?.video?.shouldAutoplay() ?? !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stopTracking = () => {
      if (trackingCallback !== null) trackingVideo?.cancelVideoFrameCallback?.(trackingCallback);
      trackingCallback = null;
    };
    const trackPresentedTime = () => {
      stopTracking();
      trackingVideo = videoFor(active);
      if (!shouldPlay() || !trackingVideo?.requestVideoFrameCallback || signal.aborted) return;
      const track = (video) => {
        trackingVideo = video;
        trackingCallback = video.requestVideoFrameCallback((_now, metadata) => {
          if (signal.aborted || preparing) return;
          if (Number.isFinite(metadata.mediaTime)) presentedTimes.set(video, metadata.mediaTime);
          const liveVideo = videoFor(active);
          if (liveVideo?.isConnected) track(liveVideo);
        });
      };
      track(trackingVideo);
    };
    const playFrame = (frame) => {
      const video = videoFor(frame);
      if (video) window.SBMediaLoader?.video.play(video);
    };
    const registerFrame = (frame) => {
      const video = videoFor(frame);
      if (!video) return;
      window.SBMediaLoader?.video.register(video, {
        offscreen: 'pause',
        hooks: {
          reveal: (target) => target.classList.add('is-frame-ready'),
          conceal: (target) => target.classList.remove('is-frame-ready'),
        },
      });
    };
    const releaseFrame = (frame) => {
      const video = videoFor(frame);
      if (video) {
        window.SBMediaLoader?.video.unregister(video);
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.remove());
        video.load();
      }
      if (frames.get(frame.dataset.heroVideoKey) === frame) frames.delete(frame.dataset.heroVideoKey);
      frame.remove();
    };
    const resumeFrame = (frame) => {
      const decoded = videoFor(frame)?.classList.contains('is-frame-ready');
      registerFrame(frame);
      if (decoded && shouldPlay()) videoFor(frame)?.classList.add('is-frame-ready');
      playFrame(frame);
      trackPresentedTime();
    };
    const waitFor = (video, events, ready, requestSignal) => new Promise((resolve) => {
      if (requestSignal.aborted) return resolve(false);
      if (ready()) return resolve(true);
      const finish = (result) => {
        window.clearTimeout(timer);
        events.forEach((event) => video.removeEventListener(event, check));
        video.removeEventListener('error', fail);
        requestSignal.removeEventListener('abort', fail);
        resolve(result);
      };
      const check = () => { if (ready()) finish(true); };
      const fail = () => finish(false);
      const timer = window.setTimeout(fail, 5000);
      events.forEach((event) => video.addEventListener(event, check));
      video.addEventListener('error', fail, { once: true });
      requestSignal.addEventListener('abort', fail, { once: true });
    });
    const waitForFrame = async (video, time, requestSignal) => {
      // Prime an unused decoder before its exact paused seek. WebKit does not
      // present a never-played video's sought frame reliably.
      if (!presentedTimes.has(video)) {
        const presented = await new Promise((resolve) => {
          if (requestSignal.aborted) return resolve(false);
          let callbackId = null;
          let settled = false;
          const finish = (ready) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            if (callbackId !== null) video.cancelVideoFrameCallback?.(callbackId);
            video.removeEventListener('playing', played);
            video.removeEventListener('error', fail);
            requestSignal.removeEventListener('abort', fail);
            video.pause();
            resolve(ready);
          };
          const played = () => finish(true);
          const fail = () => finish(false);
          const timer = window.setTimeout(fail, 5000);
          requestSignal.addEventListener('abort', fail, { once: true });
          video.addEventListener('error', fail, { once: true });
          if (video.requestVideoFrameCallback) callbackId = video.requestVideoFrameCallback(played);
          else video.addEventListener('playing', played, { once: true });
          window.SBMediaLoader?.video.play(video);
        });
        if (!presented || requestSignal.aborted) return false;
      }
      video.pause();
      const decoded = () => !video.seeking && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && Math.abs(video.currentTime - time) < 0.012;
      if (!decoded()) {
        const seeked = waitFor(video, ['seeked', 'loadeddata'], decoded, requestSignal);
        try { video.currentTime = time; }
        catch { return false; }
        if (!await seeked) return false;
      }
      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      if (requestSignal.aborted) return false;
      presentedTimes.set(video, time);
      return decoded();
    };
    const switchVideo = async () => {
      preparing?.abort();
      preparing = null;
      // Paused videos still hold WebKit decoders. Keep only the visible frame
      // and the one currently being prepared, especially during rapid clicks.
      frames.forEach((frame) => {
        if (frame === active) return;
        releaseFrame(frame);
      });
      const template = Array.from(root.querySelectorAll('template[data-hero-video-template]'))
        .find((entry) => entry.dataset.heroVideoTemplate === variantId && entry.dataset.device === device());
      if (!template || template.dataset.heroVideoKey === active.dataset.heroVideoKey) {
        resumeFrame(active);
        return;
      }
      const request = new AbortController();
      preparing = request;
      const current = () => !signal.aborted && !request.signal.aborted && preparing === request;
      const outgoing = active;
      stopTracking();
      const outgoingVideo = videoFor(outgoing);
      const outgoingDecoded = outgoingVideo.classList.contains('is-frame-ready');
      // Suspend automatic resume/recovery while its exact decoded frame is held on screen.
      window.SBMediaLoader?.video.unregister(outgoingVideo);
      outgoingVideo.pause();
      if (outgoingDecoded) outgoingVideo.classList.add('is-frame-ready');
      const incoming = template.content.firstElementChild.cloneNode(true);
      frames.set(template.dataset.heroVideoKey, incoming);
      root.append(incoming);
      incoming.setAttribute('data-hero-video-preparing', '');
      incoming.hidden = false;
      // Safari can strand the decoder of a video cloned from <template>.
      // Recreate it in the live document before loading or seeking its frames.
      const video = window.SBMediaLoader?.video?.adopt?.(videoFor(incoming)) || videoFor(incoming);
      window.SBMediaLoader?.video.unregister(video);
      video.pause();
      video.autoplay = false;
      let ready = false;
      if (!root.getClientRects().length) {
        // Buy now hides the hero. No visible frame needs matching, and hidden
        // videos cannot reliably present the frames required by that handshake.
        // Let the shared lifecycle start the selected video on Learn more.
        ready = true;
      } else if (!shouldPlay()) {
        const image = incoming.querySelector('.sb-hero-section__video-poster img');
        try { await image?.decode(); ready = Boolean(image); }
        catch { ready = false; }
      } else {
        const hasMetadata = (target) => target.readyState >= HTMLMediaElement.HAVE_METADATA && Number.isFinite(target.duration) && target.duration > 0;
        if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
        const metadata = await Promise.all([
          waitFor(video, ['loadedmetadata', 'durationchange'], () => hasMetadata(video), request.signal),
          waitFor(outgoingVideo, ['loadedmetadata', 'durationchange'], () => hasMetadata(outgoingVideo), request.signal),
        ]);
        if (current() && metadata.every(Boolean)) {
          outgoingVideo.pause();
          const time = (presentedTimes.get(outgoingVideo) ?? outgoingVideo.currentTime) % outgoingVideo.duration;
          // Frame-matched exports share duration and timeline. Never stretch progress to fit a different edit.
          if (Math.abs(video.duration - outgoingVideo.duration) < 0.012 && time < video.duration) {
            ready = await waitForFrame(video, time, request.signal);
          }
        }
      }
      if (!current()) return;
      preparing = null;
      if (!ready) {
        releaseFrame(incoming);
        resumeFrame(outgoing);
        return;
      }
      registerFrame(incoming);
      if (shouldPlay() && presentedTimes.has(video)) videoFor(incoming)?.classList.add('is-frame-ready');
      outgoing.hidden = true;
      outgoing.removeAttribute('data-hero-video-active');
      incoming.removeAttribute('data-hero-video-preparing');
      incoming.setAttribute('data-hero-video-active', '');
      active = incoming;
      releaseFrame(outgoing);
      playFrame(incoming);
      trackPresentedTime();
    };

    document.addEventListener('variant:update', (event) => {
      if (String(event.detail?.data?.productId || '') !== root.dataset.productId || !event.detail?.resource?.id) return;
      variantId = String(event.detail.resource.id);
      root.dataset.currentVariantId = variantId;
      switchVideo();
    }, { signal });
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', switchVideo, { signal });
    else {
      mobileQuery.addListener(switchVideo);
      signal.addEventListener('abort', () => mobileQuery.removeListener(switchVideo), { once: true });
    }
    controllers.set(root, () => {
      controller.abort();
      stopTracking();
      preparing?.abort();
      frames.forEach(releaseFrame);
    });
    trackPresentedTime();
  };
  const initialize = (scope = document) => {
    if (scope.matches?.(selector)) initializeHeroMedia(scope);
    scope.querySelectorAll(selector).forEach(initializeHeroMedia);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((cleanup, root) => {
      if (!event.target.contains(root)) return;
      cleanup();
      controllers.delete(root);
    });
  });
})();
